import { useCallback, useEffect, useState } from 'react';
import { notification } from 'tea-component';
import {
  chartGradientColors,
  chartGradientNOColors,
  chartAllColorsObj,
} from '@sg/utils/useChartOptions';
import * as echarts from 'echarts';
import { getOverallGrossRevenueData, getOverallProfitData } from '@sg/services/index';
import { OverallGrossRevenueColums, OverallProfitColums } from '../pageConfig';
import {
  createOverallProfitTooltipRenderer,
  createRevenueTrendTooltipRenderer,
} from '../tooltipRenderer';

// Stable module-level reference; used by both this hook and the page's RenderOperations
export const SHOW_REVENUE_TREND_LIST = ['bar', 'table'];

const PROFIT_TOOLTIP_ONLY_KEYS = new Set(['studio', 'publishing', 'casual']);
const PROFIT_VISIBLE_SERIES_KEYS = new Set(['actual_total', 'forecast_total', 'kpi', 'ForeCast']);

const getProfitBreakdownKeys = (rows: any[]): string[] => {
  const keys = new Set<string>();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (
        key === 'month'
        || key === 'yoy'
        || key === 'mom'
        || key === 'data_source'
        || key === 'total'
        || key === 'kpi'
      ) {
        return;
      }
      keys.add(key);
    });
  });

  const preferredOrder = ['studio', 'publishing', 'casual'];
  return Array.from(keys).sort((left, right) => {
    const leftOrder = preferredOrder.indexOf(left);
    const rightOrder = preferredOrder.indexOf(right);
    if (leftOrder !== -1 && rightOrder !== -1) {
      return leftOrder - rightOrder;
    }
    if (leftOrder !== -1) {
      return -1;
    }
    if (rightOrder !== -1) {
      return 1;
    }
    return left.localeCompare(right);
  });
};

const INITIAL_NOW_DATA = {
  total: { date: '', data: 0, rate: '' },
  yoy: { date: '', value: 0 },
  mom: { date: '', value: 0 },
  kpi: { date: '', data: 0, rate: '' },
};

export interface IuseTrendChartsParams {
  formatMessage: (descriptor: { id: string }) => string;
  revenueData: any;
  overallProfitData: any;
  setRevenueData: (data: any) => void;
  setOverallProfitData: (data: any) => void;
  showChatsType: any;
  showOverallProfitChatsType: any;
  chartEventSetting: { showEventBubbles: boolean };
}

export function useTrendCharts({
  formatMessage,
  revenueData,
  overallProfitData,
  setRevenueData,
  setOverallProfitData,
  showChatsType,
  showOverallProfitChatsType,
  chartEventSetting,
}: IuseTrendChartsParams) {
  // Chart payload state
  const [allData, setAllData] = useState<any>({});
  const [allOverallProfitData, setAllOverallProfitData] = useState<any>({});

  // Latest "Now" snapshot derived from API response (fed into DataProcessing/YoyMomDisplay)
  const [RevenueNowData, setRevenueNowData] = useState<any>(INITIAL_NOW_DATA);
  const [OverallNowProfitData, setOverallNowProfitData] = useState<any>(INITIAL_NOW_DATA);

  const [revenueUpdateTime, setRevenueUpdateTime] = useState<string>('');
  const [profitUpdateTime, setProfitUpdateTime] = useState<string>('');
  const [revenueTrendLoading, setRevenueTrendLoading] = useState<boolean>(true);
  const [profitTrendLoading, setProfitTrendLoading] = useState<boolean>(true);

  const fetchOverallGrossRevenueData = useCallback(async () => {
    try {
      setRevenueTrendLoading(true);
      const res: any = await getOverallGrossRevenueData({});
      setRevenueData(res?.data?.data || []);

      // Update RevenueNowData with latest actual data
      if (res?.data?.data && res.data.data.length > 0) {
        const actualData = res.data.data.filter((item: any) => item.data_source === 'actuals');
        if (actualData.length > 0) {
          const latestActual = actualData[actualData.length - 1]; // Get the last actual data
          setRevenueNowData({
            total: {
              date: latestActual.month,
              data: latestActual.total,
              // rate: '{yoy},{mom}'
              rate: `${latestActual.yoy?.total ? latestActual.yoy.total.toFixed(1) : '0'}, ${latestActual.mom?.total ? latestActual.mom.total.toFixed(1) : '0'
                }`,
            },
            yoy: {
              date: latestActual.month,
              value: latestActual.yoy?.total ?? null,
            } as any,
            mom: {
              date: latestActual.month,
              value: latestActual.mom?.total ?? null,
            } as any,
            kpi: {
              date: latestActual.month,
              data: latestActual.kpi,
              rate: '',
            },
          });
          setRevenueUpdateTime(res.data.datatime);
        }
      }
    } catch (err) {
      notification.error({
        description: `Failed to fetch overall gross revenue data: ${err}`,
      });
    } finally {
      setRevenueTrendLoading(false);
    }
  }, [setRevenueData]);

  const fetchOverallProfitData = useCallback(async () => {
    try {
      setProfitTrendLoading(true);
      const res: any = await getOverallProfitData({});
      setOverallProfitData(res?.data?.data || []);

      // Update OverallNowProfitData with latest actual data
      if (res?.data?.data && res.data.data.length > 0) {
        const actualData = res.data.data.filter((item: any) => item.data_source === 'actuals');
        if (actualData.length > 0) {
          const latestActual = actualData[actualData.length - 1]; // Get the last actual data
          setOverallNowProfitData({
            total: {
              date: latestActual.month,
              data: latestActual.total,
              rate: `${latestActual.yoy?.total ? latestActual.yoy.total.toFixed(1) : '0'}, ${latestActual.mom?.total ? latestActual.mom.total.toFixed(1) : '0'
                }`,
            },
            yoy: {
              date: latestActual.month,
              value: latestActual.yoy?.total ?? null,
            } as any,
            mom: {
              date: latestActual.month,
              value: latestActual.mom?.total ?? null,
            } as any,
            kpi: {
              date: latestActual.month,
              data: latestActual.kpi,
              rate: '',
            },
          });
          setProfitUpdateTime(res.data.datatime);
        }
      }
    } catch (err) {
      notification.error({
        description: `Failed to fetch overall profit data: ${err}`,
      });
    } finally {
      setProfitTrendLoading(false);
    }
  }, [setOverallProfitData]);

  /** Gross revenue trend card: chart + table payload → allData */
  const setGrossRevenueTrendChartData = useCallback(
    (data: any) => {
      // If there is no data or data is not an array, do nothing.
      if (!data || !Array.isArray(data) || data.length === 0) return '';
      const chartData: any = {};
      if (SHOW_REVENUE_TREND_LIST.length === 0) return;

      // Iterate over allowed display types ("bar", "table")
      SHOW_REVENUE_TREND_LIST.map((item) => {
        // Table (wrap raw data with column config)
        if (item === 'table') {
          chartData[item] = {
            data,
            columns: OverallGrossRevenueColums(formatMessage),
          };
          return '';
        }
        // Chart
        const xAxisData: any = [];
        const datas: any = {};
        const dataLength = data.length;
        const setSeriesValue = (key: string, value: any, rowIndex: number) => {
          if (!datas[key]) {
            datas[key] = new Array(dataLength).fill(undefined);
          }
          datas[key][rowIndex] = value;
        };
        // Restructure data by month index to avoid series misalignment
        // when a key (e.g. casual) is missing in some month payloads.
        data.forEach((element: any, rowIndex: number) => {
          xAxisData.push(element.month);
          Object.keys(element).forEach((key) => {
            // Ignore month, yoy, mom, data_source on graph displaying
            if (key !== 'month' && key !== 'yoy' && key !== 'mom' && key !== 'data_source') {
              // For gross revenue trend: keep each series key as-is.
              setSeriesValue(key, element[key], rowIndex);
            }
          });
        });

        // Legend mapping
        const Obj: { [key: string]: string } = {
          studio: formatMessage({ id: 'dbs_studio' }),
          publishing: formatMessage({ id: 'dbs_publishing' }),
          casual: formatMessage({ id: 'dbs_casual' }),
          total: formatMessage({ id: 'dbs_charts_legend_actual_total' }),
          actual_total: formatMessage({ id: 'dbs_charts_legend_actual_total' }),
          forecast_total: formatMessage({ id: 'dbs_charts_legend_forecast_total' }),
          actual: formatMessage({ id: 'dbs_charts_legend_actual' }),
          kpi: formatMessage({ id: 'dbs_charts_legend_kpi' }),
          // KPI: formatMessage({ id: 'dbs_charts_legend_kpi' }), // Keep for backward compatibility
          ForeCast: formatMessage({ id: 'dbs_charts_legend_forecast' }),
        };

        const originalSeriesKeys = Object.keys(datas);
        const stackedSeriesOrder: Record<string, number> = {
          casual: 0,
          studio: 1,
          publishing: 2,
        };
        // Keep a deterministic stacked render order so casual stays at the bar bottom.
        const orderedSeriesKeys = [...originalSeriesKeys].sort((left, right) => {
          const leftOrder = stackedSeriesOrder[left];
          const rightOrder = stackedSeriesOrder[right];
          const hasLeftOrder = leftOrder !== undefined;
          const hasRightOrder = rightOrder !== undefined;
          if (hasLeftOrder && hasRightOrder) {
            return leftOrder - rightOrder;
          }
          if (hasLeftOrder) {
            return -1;
          }
          if (hasRightOrder) {
            return 1;
          }
          return originalSeriesKeys.indexOf(left) - originalSeriesKeys.indexOf(right);
        });

        chartData[item] = {
          xAxisData,
          type: showOverallProfitChatsType?.value,
          seriesData: [
            ...orderedSeriesKeys
              ?.map((keys, index) => {
                // Skip the total key for RevenueTrendList
                if (keys === 'total') {
                  return null;
                }

                // Skip series that have all undefined values (e.g., forecast_total with no forecast data)
                if (datas[keys] && datas[keys].every((val: any) => val === undefined || val === null)) {
                  return null;
                }

                const tdata: any = {
                  name: Obj[keys] || keys, // Use the legend mapping or the key itself
                  stack:
                    keys === 'studio' || keys === 'publishing' || keys === 'casual'
                      ? 'total'
                      : keys === 'actual_total' || keys === 'forecast_total'
                        ? 'total'
                        : keys,
                  barWidth: '65%',
                  // NOTE: The year separator is implemented using a separate invisible line series
                  // (see addYearSeparator in utils.ts), so barWidth and barGap do not affect
                  // the separator positioning. The separator aligns perfectly with the x-axis
                  // category positions regardless of bar settings.
                  color: keys === 'forecast_total' ? chartGradientNOColors[1]?.[1] : chartAllColorsObj[keys],
                  // color: chartAllColorsObj[keys],
                  data: datas[keys]?.map((items: any, i: number) => {
                    if (showOverallProfitChatsType?.value === 'bar' && keys !== 'kpi') {
                      // Overall Gross Revenue graph data
                      const currentData = data[i];
                      const isForecast = currentData.data_source === 'forecast';
                      const tempsData: any = {
                        value: items,
                        seriesKey: keys,
                        studio: datas.studio[i] ?? null,
                        publishing: datas.publishing[i] ?? null,
                        casual: datas.casual[i] ?? null,
                        tooltipWidth: '350px',
                        names: 'Total,YoY,MoM',
                        render: createRevenueTrendTooltipRenderer(keys, index, Obj, isForecast),
                        itemStyle: {
                          opacity: isForecast ? 0.4 : 1,
                        },
                      };
                      // Add YoY/MoM data from API

                      const formatPercentage = (value: number | null | undefined) => {
                        if (value === null || value === undefined || isNaN(value)) {
                          return 'NA';
                        }
                        return `${value.toFixed(1)}%`;
                      };

                      tempsData.total = currentData.total ?? null;
                      tempsData.yoy = currentData.yoy
                        ? {
                          total: formatPercentage(currentData.yoy.total),
                          publishing: formatPercentage(currentData.yoy.publishing),
                          studio: formatPercentage(currentData.yoy.studio),
                          casual: formatPercentage(currentData.yoy.casual),
                        }
                        : {
                          total: 'NA',
                          publishing: 'NA',
                          studio: 'NA',
                          casual: 'NA',
                        };
                      tempsData.mom = currentData.mom
                        ? {
                          total: formatPercentage(currentData.mom.total),
                          publishing: formatPercentage(currentData.mom.publishing),
                          studio: formatPercentage(currentData.mom.studio),
                          casual: formatPercentage(currentData.mom.casual),
                        }
                        : {
                          total: 'NA',
                          publishing: 'NA',
                          studio: 'NA',
                          casual: 'NA',
                        };

                      return tempsData;
                    }
                    return {
                      value: items,
                      seriesKey: keys,
                    };
                  }),
                  type:
                    keys === 'kpi' && showOverallProfitChatsType?.value === 'bar'
                      ? 'line'
                      : showOverallProfitChatsType?.value,
                  z: keys === 'kpi' || keys === 'ForeCast' ? 99 : orderedSeriesKeys.length - index,
                  // showSymbol: showChatsType?.format,
                  // Add white border separator for stacked bar charts (studio, publishing, actual_total, forecast_total)
                  ...(showChatsType?.value === 'bar' && (keys === 'studio' || keys === 'publishing' || keys === 'casual')
                    ? {
                      itemStyle: {
                        borderColor: '#fff',
                        borderWidth: 0.5,
                      },
                    }
                    : {}),
                };
                return tdata;
              })
              .filter(Boolean),
          ], // Filter out null values from skipped series
        };

        // Keep a trailing empty category only for Gross Revenue Trend.
        // This matches the original chart spacing behavior for this card.
        xAxisData.push('');

        // Keep all revenue series aligned with xAxis length.
        // This prevents ECharts hover/index resolution errors when an extra trailing xAxis slot exists.
        chartData[item].seriesData = (chartData[item].seriesData || []).map((series: any) => {
          const sData = Array.isArray(series?.data) ? series.data : [];
          if (sData.length < xAxisData.length) {
            return {
              ...series,
              data: [...sData, ...new Array(xAxisData.length - sData.length).fill(undefined)],
            };
          }
          return series;
        });

        // Add hidden series for EventWindows positioning on Gross Revenue Trend chart.
        if (chartEventSetting.showEventBubbles) {
          chartData[item].seriesData.push({
            name: 'TopTipsEvent',
            type: showChatsType?.value === 'bar' ? 'bar' : 'line',
            ...(showChatsType?.value === 'bar'
              ? {
                barWidth: '65%',
                barGap: '-100%',
              }
              : {}),
            data: new Array(xAxisData.length).fill(0),
            itemStyle: { color: 'transparent', opacity: 0 },
            lineStyle: { opacity: 0 },
            label: { show: false },
            tooltip: { show: false },
            silent: true,
            z: -1,
            hiddenFromLegend: true,
          });
        }

      });

      return setAllData(chartData);
    },
    [formatMessage, showChatsType, chartEventSetting.showEventBubbles],
  );

  /** Overall profit trend card: chart + table payload → allOverallProfitData */
  const setOverallProfitTrendChartData = useCallback(
    (data: any) => {
      // If there is no data or data is not an array, do nothing.
      if (!data || !Array.isArray(data) || data.length === 0) return '';
      const chartData: any = {};
      if (SHOW_REVENUE_TREND_LIST.length === 0) return;

      // Iterate over allowed display types ("bar", "table")
      SHOW_REVENUE_TREND_LIST.map((item) => {
        // Table (wrap raw data with column config)
        if (item === 'table') {
          chartData[item] = {
            data,
            columns: OverallProfitColums(formatMessage),
          };
          return '';
        }
        // Chart
        const xAxisData: any = [];
        const datas: any = {};
        const dataLength = data.length;
        const setSeriesValue = (key: string, value: any, rowIndex: number) => {
          if (!datas[key]) {
            datas[key] = new Array(dataLength).fill(undefined);
          }
          datas[key][rowIndex] = value;
        };
        // Restructure data by month index to keep all series aligned.
        data.forEach((element: any, rowIndex: number) => {
          xAxisData.push(element.month);
          Object.keys(element).forEach((key) => {
            // Ignore month, yoy, mom, data_source on graph displaying
            if (key !== 'month' && key !== 'yoy' && key !== 'mom' && key !== 'data_source') {
              if (key === 'total') {
                // Split total into actual_total and forecast_total based on data_source (OverallProfit)
                if (element.data_source === 'actuals' || element.data_source === 'none') {
                  setSeriesValue('actual_total', element[key], rowIndex);
                  setSeriesValue('forecast_total', undefined, rowIndex);
                } else if (element.data_source === 'forecast') {
                  setSeriesValue('actual_total', undefined, rowIndex);
                  setSeriesValue('forecast_total', element[key], rowIndex);
                }
              } else {
                // For other keys, keep original logic
                setSeriesValue(key, element[key], rowIndex);
              }
            }
          });
        });
        // xAxisData.push('');

        // Legend mapping
        const Obj: { [key: string]: string } = {
          studio: formatMessage({ id: 'dbs_studio' }),
          publishing: formatMessage({ id: 'dbs_publishing' }),
          casual: formatMessage({ id: 'dbs_casual' }),
          total: formatMessage({ id: 'dbs_charts_legend_actual_total' }),
          actual_total: formatMessage({ id: 'dbs_charts_legend_actual_total' }),
          forecast_total: formatMessage({ id: 'dbs_charts_legend_forecast_total' }),
          actual: formatMessage({ id: 'dbs_charts_legend_actual' }),
          kpi: formatMessage({ id: 'dbs_charts_legend_kpi' }),
          // KPI: formatMessage({ id: 'dbs_charts_legend_kpi' }), // Keep for backward compatibility
          ForeCast: formatMessage({ id: 'dbs_charts_legend_forecast' }),
        };
        const breakdownKeys = getProfitBreakdownKeys(data);

        chartData[item] = {
          xAxisData,
          type: showOverallProfitChatsType?.value,
          seriesData: [
            ...Object.keys(datas)
              ?.map((keys, index) => {
                // Skip the studio and publishing keys for OverallProfit
                if (PROFIT_TOOLTIP_ONLY_KEYS.has(keys) || !PROFIT_VISIBLE_SERIES_KEYS.has(keys)) {
                  return null;
                }

                // Skip series that have all undefined values (e.g., forecast_total with no forecast data)
                if (datas[keys] && datas[keys].every((val: any) => val === undefined || val === null)) {
                  return null;
                }

                const tdata: any = {
                  name: Obj[keys] || keys, // Use the legend mapping or the key itself
                  stack: keys === 'actual_total' || keys === 'forecast_total' ? 'total' : keys,
                  barWidth: '65%',
                  // NOTE: The year separator is implemented using a separate invisible line series
                  // (see addYearSeparator in utils.ts), so barWidth and barGap do not affect
                  // the separator positioning. The separator aligns perfectly with the x-axis
                  // category positions regardless of bar settings.
                  color: keys === 'forecast_total' ? chartGradientNOColors[1]?.[1] : chartAllColorsObj[keys],
                  // color: chartAllColorsObj[keys],
                  data: datas[keys]?.map((items: any, i: number) => {
                    if (showOverallProfitChatsType?.value === 'bar' && keys !== 'kpi') {
                      // For actual_total and forecast_total, always create data with render function
                      // Bar chart with custom tooltip
                      const isForecast = keys === 'forecast_total';
                      const isNegative = items !== undefined && items !== null && Number(items) < 0;
                      const breakdownValues = breakdownKeys.reduce((acc, breakdownKey) => {
                        acc[breakdownKey] = datas[breakdownKey]?.[i];
                        return acc;
                      }, {} as Record<string, any>);

                      return {
                        value: items,
                        ...breakdownValues,
                        // Profit trend: same as before when type was OverallProfit (not RevenueTrendList)
                        tooltipWidth: '',
                        names: '',
                        render: createOverallProfitTooltipRenderer(
                          index,
                          Obj,
                          isForecast,
                          breakdownKeys,
                        ),
                        itemStyle: {
                          // Apply gradient color for profit trend bars
                          // For negative bars, reverse gradient so lighter color starts from 0 (top)
                          color:
                            keys === 'actual_total'
                              ? new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                {
                                  offset: 0,
                                  color: isNegative ? chartGradientColors[1]?.[0] : chartGradientColors[1]?.[1],
                                }, // Top: lighter for negative, darker for positive
                                {
                                  offset: 1,
                                  color: isNegative ? chartGradientColors[1]?.[1] : chartGradientColors[1]?.[0],
                                }, // Bottom: darker for negative, lighter for positive
                              ])
                              : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                {
                                  offset: 0,
                                  color: isNegative ? chartGradientNOColors[1]?.[0] : chartGradientNOColors[1]?.[1],
                                }, // Top: lighter for negative, darker for positive
                                {
                                  offset: 1,
                                  color: isNegative ? chartGradientNOColors[1]?.[1] : chartGradientNOColors[1]?.[0],
                                }, // Bottom: darker for negative, lighter for positive
                              ]),
                          // opacity: isForecast ? 0.3 : 1,
                        },
                      };
                    }
                    return {
                      value: items,
                    };
                  }),
                  type: keys === 'kpi' && showOverallProfitChatsType?.value === 'bar'
                    ? 'line'
                    : showOverallProfitChatsType?.value,
                  z: keys === 'kpi' || keys === 'ForeCast' ? 99 : Object.keys(datas).length - index,
                  // showSymbol: showChatsType?.format,
                  // Add white border separator for stacked bar charts (studio, publishing, actual_total, forecast_total)
                  ...(showOverallProfitChatsType?.value === 'bar' && (keys === 'studio' || keys === 'publishing')
                    ? {
                      itemStyle: {
                        borderColor: '#fff',
                        borderWidth: 0.5,
                      },
                    }
                    : {}),
                };
                return tdata;
              })
              .filter(Boolean),
          ], // Filter out null values from skipped series
        };

      });

      return setAllOverallProfitData(chartData);
    },
    [formatMessage, showOverallProfitChatsType],
  );

  // Rebuild gross revenue chart payload whenever underlying data or chart-mode/event toggle changes
  useEffect(() => {
    setGrossRevenueTrendChartData(revenueData);
  }, [revenueData, setGrossRevenueTrendChartData]);

  // Rebuild profit chart payload whenever underlying data or chart-mode changes
  useEffect(() => {
    setOverallProfitTrendChartData(overallProfitData);
  }, [overallProfitData, setOverallProfitTrendChartData]);

  return {
    allData,
    allOverallProfitData,
    revenueTrendLoading,
    profitTrendLoading,
    RevenueNowData,
    OverallNowProfitData,
    revenueUpdateTime,
    profitUpdateTime,
    fetchOverallGrossRevenueData,
    fetchOverallProfitData,
    setGrossRevenueTrendChartData,
    setOverallProfitTrendChartData,
  };
}
